import clsx from 'clsx';
import { forwardRef } from 'react';
import type { HTMLAttributes, JSX } from 'react';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';

const currentYear = new Date().getFullYear();

interface CopyrightFooterProps extends HTMLAttributes<HTMLElement> {
  variant?: 'default' | 'compact';
}

export const CopyrightFooter = forwardRef<HTMLElement, CopyrightFooterProps>(
  function CopyrightFooter({ className, variant = 'default', ...rest }, ref): JSX.Element {
    const siteSettingsQuery = useSiteSettingsDetail();
    const containerPadding = variant === 'compact' ? 'py-4 md:py-4' : 'py-6 md:py-6';
    const companyName = (siteSettingsQuery.data?.footer?.companyName ?? '').trim();
    const resolvedCompanyName = companyName.length > 0 ? companyName : 'Strashin Developments';
    const configuredCopyright = (siteSettingsQuery.data?.footer?.copyrightText ?? '').trim();
    const fallbackCopyright = `© {year} ${resolvedCompanyName}. All rights reserved.`;
    const resolvedCopyrightTemplate =
      configuredCopyright.length > 0 ? configuredCopyright : fallbackCopyright;
    const resolvedCopyrightText = resolvedCopyrightTemplate.replaceAll(
      '{year}',
      String(currentYear),
    );

    return (
      <footer
        ref={ref}
        {...rest}
        data-testid="site-footer"
        className={clsx('border-t border-surface-subtle bg-surface-cream', className)}
      >
        <div
          className={clsx(
            'container flex flex-col gap-sm md:flex-row md:items-center md:justify-between',
            containerPadding,
          )}
        >
          <span className="font-heading text-lg text-brand-primary">{resolvedCompanyName}</span>
          <p className="text-sm text-text-primary/60">{resolvedCopyrightText}</p>
        </div>
      </footer>
    );
  },
);
